import { useTranslation } from 'react-i18next';
import moment from 'moment';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ColorRing } from 'react-loader-spinner';

import { statusClass, statusTitle } from '../utils';

import SMSTMonitoringFilter from './FilterSMST/FilterSMST';

import useGetSTMonitoring from 'hooks/Reports/STMonitoring/MCTMonitoring';
import { TrapTypeMonitoring, SignalTime } from 'api/reports/types';

import './SMSTMonitoring.scss';

const SMSTMonitoring: React.FC<{ slug: string }> = ({ slug }) => {
  const { t } = useTranslation();

  const initialStartDate = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
  const initialEndDate = moment().format('YYYY-MM-DD');

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [filterType, setFilterType] = useState<string>('default');

  const { data, refetch, isRefetching, isLoading } = useGetSTMonitoring(
    slug,
    TrapTypeMonitoring.SMST,
    startDate,
    endDate,
    filterType,
  );
  const [isDateChanged, setIsDateChanged] = useState(false);

  useEffect(() => {
    if (!isDateChanged) {
      refetch();
    }
  }, [startDate, endDate, refetch, isDateChanged, filterType]);

  const handleLastMonth = () => {
    setStartDate(moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'));
    setIsDateChanged(false);
  };

  const handleCurrentMonth = () => {
    setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().format('YYYY-MM-DD'));
    setIsDateChanged(false);
  };

  const handleApplyDateChange = () => {
    setIsDateChanged(false);
    refetch();
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setIsDateChanged(true);
  };

  return (
    <div>
      <div className="bg-white">
        <div className="card">
          <div className="card-body">
            <div className="row my-3">
              <div className="d-flex flex-column flex-lg-row justify-content-center justify-content-lg-between align-items-center my-3 gap-3">
                <div className="d-flex flex-column flex-lg-row gap-2 gap-lg-0 align-items-center">
                  <div className="d-flex flex-column flex-lg-row gap-2 gap-lg-0 align-items-center">
                    <input
                      type="date"
                      className="form-control mr-2"
                      value={startDate}
                      onChange={e => handleDateChange(e.target.value, endDate)}
                    />
                    <input
                      type="date"
                      className="form-control mx-2"
                      value={endDate}
                      onChange={e => handleDateChange(startDate, e.target.value)}
                    />

                    <button
                      className="btn btn-ghost-primary rounded-pill mx-2"
                      style={{ width: '200px' }}
                      onClick={handleApplyDateChange}
                    >
                      Apply
                    </button>
                  </div>
                  <div className="d-flex gap-2 mt-2 mt-lg-0">
                    <button className="btn px-4 mx-3 btn-ghost-primary rounded-pill" onClick={handleLastMonth}>
                      {moment().subtract(1, 'months').format('MMMM')}
                    </button>
                    <button className="btn px-4 mx-3 btn-ghost-primary rounded-pill" onClick={handleCurrentMonth}>
                      {moment().format('MMMM')}
                    </button>
                  </div>
                </div>
                <SMSTMonitoringFilter filterType={filterType} setFilterType={setFilterType} />
              </div>
            </div>

            {isRefetching || isLoading ? (
              <div className="row">
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1001,
                    height: '70vh',
                  }}
                >
                  <ColorRing
                    visible
                    height="80"
                    width="80"
                    ariaLabel="color-ring-loading"
                    wrapperStyle={{}}
                    wrapperClass="color-ring-wrapper"
                    colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
                  />
                </div>
              </div>
            ) : (
              <div className="row" style={{ overflowX: 'auto' }}>
                <div className="table-wrapper" style={{ minWidth: '1440px', maxWidth: '100%', overflowX: 'auto' }}>
                  <table className="mct-table">
                    <thead className="mct-thead">
                      <tr className="ml-th-bordered-bottom-dark">
                        <th
                          className="text-center mct-th table-sticky-header"
                          scope="col"
                          style={{ position: 'sticky', top: 0, left: 0, backgroundColor: '#e7d7ea', zIndex: 2 }}
                        >
                          {t('VisitReportsPDF.monitoring-table.trap-number')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.trap-serial-number')}
                        </th>

                        <th className="text-center mct-th install-date table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.install-date')}
                        </th>

                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.signal-dates')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.total-triggers')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.signal-time')}
                        </th>
                        <th
                          className="text-center mct-th table-sticky-header"
                          scope="col"
                          title={t('VisitReportsPDF.monitoring-table.statusTooltip')}
                        >
                          {t('VisitReportsPDF.monitoring-table.trigger-type')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.enter-side')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.mice-count')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="mct-tbody">
                      {data && data.length > 0 ? (
                        data.flatMap((monitoring, index) => {
                          const snaps = monitoring.snaps ?? [];

                          return snaps.flatMap((snap, snapIndex) => {
                            const signals = (snap.signals ?? []).sort(
                              (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf(),
                            );

                            // total rows for rowSpan of trap cells
                            const totalRowSpan = signals.reduce((sum, s) => sum + Math.max(1, s.time?.length ?? 0), 0);

                            // your border logic (fix the always-true condition)
                            const getModuleBase = (seq?: string) => String(seq ?? '').split('-')[0];
                            const thisBase = getModuleBase(snap.sequence_number);
                            const prevBase =
                              snapIndex > 0 ? getModuleBase(snaps[snapIndex - 1]?.sequence_number) : null;
                            const setBorder = !!(prevBase && prevBase === thisBase);

                            let printedTrapCells = false;

                            return signals.flatMap((s, dayIndex) => {
                              const rows: SignalTime[] = s.time;

                              return rows.map((timeObj, timeIndex) => {
                                const dateLabel = dayjs(s.date).isValid()
                                  ? dayjs(s.date).format('YYYY-MM-DD')
                                  : String(s.date ?? '---');
                                const timeLabel = timeObj?.inner_time ?? '---';

                                const triggerType = timeObj.action_type;
                                const miceCount = typeof timeObj?.amount === 'number' ? timeObj.amount : '---';

                                const renderTrapCells = !printedTrapCells && dayIndex === 0 && timeIndex === 0;
                                if (renderTrapCells) printedTrapCells = true;
                                return (
                                  <tr
                                    key={`${monitoring.serial_number}-${snap.sequence_number}-${index}-${snapIndex}-${dayIndex}-${timeIndex}`}
                                    className={!setBorder ? 'mct-paired-row' : undefined}
                                  >
                                    {renderTrapCells && (
                                      <>
                                        <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                                          <div className="sticky-cell">{snap.sequence_number || 'N/A'}</div>
                                        </td>
                                        <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                                          <div className="sticky-cell">{monitoring.serial_number || 'N/A'}</div>
                                        </td>
                                        <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                                          <div className="sticky-cell">{monitoring.date_of_installation || 'N/A'}</div>
                                        </td>
                                      </>
                                    )}

                                    {timeIndex === 0 && (
                                      <td className="text-center align-top mct-td" rowSpan={rows.length}>
                                        {dateLabel}
                                      </td>
                                    )}

                                    {timeIndex === 0 && (
                                      <td className="text-center align-top mct-td" rowSpan={rows.length}>
                                        {s.action_count ?? 0}
                                      </td>
                                    )}

                                    <td className="text-center mct-td signal-time">{timeLabel}</td>
                                    <td
                                      className={`text-center mct-td signal-time text-${statusClass(triggerType)}`}
                                      title={
                                        timeObj.approved_at
                                          ? t('VisitReportsPDF.monitoring-table.updatedStatusTooltip', {
                                              date: dayjs(timeObj.approved_at).format('YYYY-MM-DD HH:mm:ss'),
                                            })
                                          : undefined
                                      }
                                    >
                                      {statusTitle(triggerType, t)} {timeObj.approved_at && '*'}
                                    </td>
                                    <td className="text-center align-top mct-td">
                                      {snap.enter && s.action_count > 0
                                        ? `Trap ${snap.enter === 'R' ? '2' : '1'} (${snap.enter})`
                                        : '---'}
                                    </td>
                                    <td className="text-center mct-td signal-time">
                                      <strong>{miceCount}</strong>
                                    </td>
                                  </tr>
                                );
                              });
                            });
                          });
                        })
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center mct-td">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSTMonitoring;
